import { baseApi, API_TAGS, createCacheTags, createEndpointBuilder } from '@/shared/api';
import type { ErrorResponse } from '@/shared/api/types';

const palletsEndpoint = createEndpointBuilder('/api/v2/consignments');

/**
 * Tags to invalidate after any pallet mutation.
 *
 * Pallet data (the `pallets` array + each line's pallet assignment) is served by
 * the consignment lines endpoint, so every pallet change must invalidate the
 * exact tag that `getConsignmentLines` provides. The params shape/order here must
 * match that query: `{ projectId, siteId, consignmentId }`.
 *
 * Scope is intentionally limited to the lines list — same as the consignment line
 * mutations. We do NOT invalidate the whole consignment (the consignment-by-id
 * query), since a pallet/line change doesn't require refetching consignment details.
 */
const invalidateConsignmentLinesTags = ({
  consignmentId,
  projectId,
  siteId
}: {
  consignmentId: string;
  projectId?: number;
  siteId?: number;
}) =>
  createCacheTags({
    baseTag: API_TAGS.CONSIGNMENTS,
    params: { projectId, siteId, consignmentId },
    includeBaseTag: false
  });

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Pallet types accepted by the backend.
 *
 * The create-pallet endpoints resolve `type` against the `overpacking_type`
 * table, either by numeric id or by exact `description` match. The seeded
 * descriptions are "Warehouse Pallet" and "Wooden Crate" — NOT the
 * `warehouse_pallet`/`wooden_crate` snake_case shown as examples in the API
 * docs (those values are rejected with "Invalid pallet type"). Revert to the
 * snake_case values once the backend accepts them.
 */
export const PALLET_TYPES = {
  WAREHOUSE_PALLET: 'Warehouse Pallet',
  WOODEN_CRATE: 'Wooden Crate'
} as const;

export type PalletType = (typeof PALLET_TYPES)[keyof typeof PALLET_TYPES];

// ─── Request/Response Types ──────────────────────────────────────────────────

/** Request body for creating a single pallet */
export interface CreatePalletRequest {
  /** Type of pallet */
  type: PalletType;
  /** Inventory unit: e.g. 'boxes', 'Each', etc. */
  inventoryUnit?: string;
  /** Number of inventory units per transport unit (optional) */
  inventoryUnitsPerTransport?: number;
  /** Length in centimeters (optional) */
  length?: number;
  /** Width in centimeters (optional) */
  width?: number;
  /** Height in centimeters (optional) */
  height?: number;
  /** Maximum weight in kilograms (optional) */
  maxWeight?: number;
}

/** Response from creating a single pallet */
export interface CreatePalletResponse {
  id: number;
  palletNumber: string;
  type: PalletType;
  consignmentId: number;
}

export interface CreatePalletParams {
  consignmentId: string;
  projectId?: number;
  siteId?: number;
}

/** Request body for bulk creating pallets */
export interface CreatePalletBulkRequest {
  /** Type of pallet */
  type: PalletType;
  /** Number of pallets to create */
  quantity: number;
  /** Inventory unit: e.g. 'boxes', 'Each', etc. */
  inventoryUnit: string;
  /** Number of inventory units per transport unit */
  inventoryUnitsPerTransport: number;
  /** IDs of consignment lines to distribute across pallets */
  lineIds?: string[];
  /** Whether to split lines across pallets if needed */
  splitLines?: boolean;
}

export interface CreatePalletBulkParams {
  consignmentId: string;
  projectId?: number;
  siteId?: number;
}

/** Request to assign/unassign line to pallet */
export interface AssignLineToPalletRequest {
  /** Full pallet UUID to assign to, or null to unassign */
  palletUuid: string | null;
}

export interface AssignLineToPalletParams {
  consignmentId: string;
  lineId: string;
  projectId?: number;
  siteId?: number;
}

export interface DeletePalletParams {
  consignmentId: string;
  palletId: string;
  projectId?: number;
  siteId?: number;
}

// ─── API Slice ───────────────────────────────────────────────────────────────

/**
 * Pallet API endpoints.
 *
 * - Pallet number is auto-generated by the backend on creation.
 * - There is no dedicated GET pallets endpoint; pallet data for display is
 *   derived client-side from the consignment lines endpoint
 *   (`/api/v2/consignments/{id}/lines`) — each line's `palletIds`/`palletNames`
 *   arrays determine its pallet grouping (a line may belong to several pallets).
 *   See `derivePalletsFromLines`.
 */
export const palletsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Create a single pallet for a consignment.
     * Pallet number is auto-generated by the backend.
     */
    createPallet: builder.mutation<CreatePalletResponse, CreatePalletRequest & CreatePalletParams, ErrorResponse>({
      query: ({ consignmentId, projectId, siteId, ...body }) => ({
        url: palletsEndpoint(`/${consignmentId}/pallets`),
        method: 'POST',
        params: { projectId, siteId },
        body
      }),
      invalidatesTags: (_result, _error, arg) => invalidateConsignmentLinesTags(arg)
    }),

    /**
     * Create multiple pallets and distribute lines across them.
     * Lines are split across pallets respecting capacity.
     */
    createPalletBulk: builder.mutation<
      CreatePalletResponse[],
      CreatePalletBulkRequest & CreatePalletBulkParams,
      ErrorResponse
    >({
      query: ({ consignmentId, projectId, siteId, ...body }) => ({
        url: palletsEndpoint(`/${consignmentId}/pallets/bulk`),
        method: 'POST',
        params: { projectId, siteId },
        body
      }),
      invalidatesTags: (_result, _error, arg) => invalidateConsignmentLinesTags(arg)
    }),

    /**
     * Delete a pallet by ID from a consignment.
     * All assigned lines become unassigned.
     * Split lines are merged back into parent.
     */
    deletePallet: builder.mutation<void, DeletePalletParams, ErrorResponse>({
      query: ({ consignmentId, palletId, projectId, siteId }) => ({
        url: palletsEndpoint(`/${consignmentId}/pallets/${palletId}`),
        method: 'DELETE',
        params: { projectId, siteId }
      }),
      invalidatesTags: (_result, _error, arg) => invalidateConsignmentLinesTags(arg)
    }),

    /**
     * Assign or unassign a consignment line to/from a pallet.
     * Pallets are identified by their full UUID; set palletUuid to null to unassign.
     */
    assignLineToPallet: builder.mutation<void, AssignLineToPalletRequest & AssignLineToPalletParams, ErrorResponse>({
      query: ({ consignmentId, lineId, projectId, siteId, palletUuid }) => ({
        url: palletsEndpoint(`/${consignmentId}/lines/${lineId}/pallet`),
        method: 'PUT',
        params: { projectId, siteId },
        body: { palletUuid }
      }),
      invalidatesTags: (_result, _error, arg) => invalidateConsignmentLinesTags(arg)
    })
  })
});

export const {
  useCreatePalletMutation,
  useCreatePalletBulkMutation,
  useDeletePalletMutation,
  useAssignLineToPalletMutation
} = palletsApi;
